import React from "react";
import { Navigate } from "react-router-dom";
import { useRBAC } from "@/contexts/RBACContext";
import { useAuth } from "@/contexts/AuthContext";
import { GlassCard } from "@/components/glass/GlassCard";
import { ShieldWarning } from "@phosphor-icons/react";

/**
 * Wrap a protected route with RBAC module gating:
 *   <ModuleGuard module="studio"><ContractStudio /></ModuleGuard>
 *
 * While RBAC is loading, renders children (no flash-of-empty).
 * If the user's role is not in the allowed modules list, shows a 403 card.
 */
export default function ModuleGuard({ module, children }) {
  const { user } = useAuth();
  const { allowed, ready } = useRBAC();

  if (!user) return <Navigate to="/login" replace />;
  if (user.is_global) return children;
  if (!ready) return children;
  if (!module) return children;
  if (allowed.has(module)) return children;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <GlassCard className="p-8 max-w-md text-center" data-testid="module-forbidden">
        <ShieldWarning size={32} weight="fill" className="mx-auto mb-3 text-amber-500" />
        <h2 className="text-xl font-semibold mb-1">You don't have access to this screen</h2>
        <p className="text-sm text-[var(--text-3)]">
          Your current role (<code className="px-1.5 py-0.5 rounded bg-[var(--surface-2)]">{user.role}</code>) does not include <strong>{module}</strong>.
          Ask your tenant admin to update your role in <em>Roles & Permissions</em>.
        </p>
      </GlassCard>
    </div>
  );
}
